/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useEffect, useState} from 'react';
import {Col, Row} from "react-bootstrap";
import {ConfirmationDialog} from "../../../../Common/ConfirmationDialog";
import {
    Species
} from "../../../../../Models";
import {useGetContext} from "../../../../../Hooks/RouteHooks";
import {usePrimeCache} from "../../../../../Hooks/ServiceHooks";
import {useValidations} from "../../../../../Hooks/GenericModelHooks";
import {service as SpeciesService} from "../../../../../Services/SpeciesService";
import {service as SpeciesRequestSourceService} from "../../../../../Services/SpeciesRequestSourceService";
import {service as SpeciesRequestStatusService} from "../../../../../Services/SpeciesRequestStatusService";
import {service as SpeciesListService} from "../../../../../Services/SpeciesListService";
import {GenericModelViewer} from "../../../../Common/GenericModelViewer";
import {default as DateUtils} from "../../../../../../../monitoring/src/Utils/DateUtils";
import {useScrollToRef} from "../../../../../Hooks/ScrollHooks";

export function SpeciesRequestModal(props) {
    const {onHide, show, speciesRequestSourceCode = 'sba-global'} = props;

    usePrimeCache([SpeciesRequestSourceService, SpeciesRequestStatusService]);

    const getContext = useGetContext();
    const currentContext = getContext();

    const [errorElement, showErrorElement] = useScrollToRef(null);

    const defaultSpecies = {
        speciesTypeId: 5, // other
        SpeciesRequest: {
            observedProjectContextNodeId: currentContext?.projectContextNodeId,
            observedDate: DateUtils.GetCurrentDateTime(),
            _speciesListIds: [],
        },
    }

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [species, setSpecies] = useState(defaultSpecies);
    const [validations, setValidations, isValid] = useValidations();

    const observedProjectContextNodeId = species?.SpeciesRequest?.observedProjectContextNodeId;

    useEffect(() => {
        if(show) {
            setSuccess(false);
            setSpecies(defaultSpecies);
        }
    }, [show]);

    const automagicallySetSpeciesListIdByContextNodeId = async (contextNodeId) => {
        try {
            const speciesListForObservedProject =
                (await SpeciesListService.GetSpeciesListByContextNodeId(contextNodeId))?.result;

            if(!species?.SpeciesRequest?._speciesListIds?.length) {
                setSpecies({
                    ...species,
                    SpeciesRequest: {
                        ...species?.SpeciesRequest,
                        _speciesListIds: [speciesListForObservedProject?.speciesListId],
                    }
                });
            }
        } catch (e) {
            console.log('failed to get speciesListId for contextNodeId: ', contextNodeId);
        }
    }

    useEffect(() => {
        if(observedProjectContextNodeId) {
            void automagicallySetSpeciesListIdByContextNodeId(observedProjectContextNodeId);
        }
    }, [observedProjectContextNodeId]);

    const saveSpecies = async ()  => {
        setError(null);
        try {
            const response = await SpeciesService.CreateSpecies(species);
            setSuccess(response?.result);
        } catch (errorResponse) {
            setError(errorResponse?.message);
            showErrorElement();
        }
    };

    return <ConfirmationDialog
        size="lg"
        Title="Request New Species"
        Body={[
            (success) &&
            <Row key={1}>
                <Col xs={12}>
                    <h2>Thank you!</h2>
                    <p className="">Your request has been submitted. You will be notified by e-mail after it has been reviewed.</p>
                </Col>
            </Row>,
            (error) &&
            <Row key={2}>
                <Col xs={12}>
                    <p className="sb-model-viewer-validation-failure-text" ref={errorElement}>{error}</p>
                </Col>
            </Row>,
            (!success) && <Row key={3}>
                <Col xs={12}>
                    <GenericModelViewer
                        IsNewModel={true}
                        Model={new Species()}
                        EditMode={true}
                        Data={species}
                        Update={setSpecies}
                        HideGroups={[]}
                        HideFields={[
                            'speciesTypeId',
                            'speciesNativityId',
                            'speciesInvasivityId',
                            'isDefault',
                            'fulcrumId',
                            'mobileSampleSpeciesId',
                            'SpeciesRequest_reviewerNotes',
                            'SpeciesRequest_requestedByUserId',
                        ]}
                        CustomData={{}}
                        OnValidationUpdate={setValidations}
                    />
                </Col>
            </Row>,
        ]}
        Display={show}
        OnHide={onHide}
        OnClose={onHide}
        CancelButtonMessage={success ? "Close" : "Cancel"}
        OnConfirm={success ? null : saveSpecies}
        ConfirmationButtonMessage={success ? null : "Save"}
        DisableConfirmationButton={!isValid()}
    />;
}
